import { shallowEqual, useSelector } from 'react-redux';
import {
  useCallback, useEffect, useRef
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useShakaPlayer from './useShakaPlayer';
import sendStreamEvent from '@/helpers/api/events/sendStreamEvent';
import {
  selectAudioPaused,
  selectAudioStreamingContext,
  selectCurrentEpisode,
  selectCurrentEpisodeKey
} from '@/state/selectors/audio';
import useGA from '@/hooks/ga/useGA';
import useIntercom from '@/hooks/intercom/useIntercom';
import useFBPixel from '@/hooks/fb/useFBPixel';
import {
  GAstreamedEpisodeStart,
  GAstreamedEpisode,
  GAstreamedGenreStart,
  GAstreamedGenre
} from '@/helpers/events-ga/audio';
import {
  ICstreamedEpisodeStart,
  ICstreamedEpisode,
  ICstreamedGenreStart,
  ICstreamedGenre
} from '@/helpers/events-intercom/audio';
import {
  FBstreamedEpisodeStart,
  FBstreamedEpisode
} from '@/helpers/events-fb-pixel/audio';
import clearShowEdgeById from '@/react-query/edges/utils/clearShowEdgeById';

const selector = (state) => ({
  currentEpisodeKey: selectCurrentEpisodeKey(state),
  isPaused: selectAudioPaused(state),
  currentEpisode: selectCurrentEpisode(state),
  currentStreamingContext: selectAudioStreamingContext(state)
});

const useAudioStreamEvents = () => {
  const {
    isPaused,
    currentEpisode,
    currentStreamingContext
  } = useSelector(selector, shallowEqual);
  const {
    trackKey,
    getPreviousTrackStats,
    getCurrentTrackStats
  } = useShakaPlayer();
  const queryClient = useQueryClient();
  const currentTrackKeyRef = useRef();
  const currentEpisodeRef = useRef();
  const currentTrackStreamStartSentRef = useRef(false);
  const browserCloseStreamSentRef = useRef(false);
  const { sendGAEvent } = useGA();
  const { sendFBPixelCustomEvent } = useFBPixel();
  const { sendIntercomEvent } = useIntercom();

  const handleSendStreamStartEvent = useCallback(({ episode } = {}) => {
    const {
      id: episodeId,
      episode_number: episodeNumber,
      season: seasonNumber,
      genres
    } = episode || {};

    if (episodeId) {
      currentTrackStreamStartSentRef.current = true;
      
      const episodeShow = (episode || {}).show;
      const { id: showId, title: showTitle } = episodeShow;

      const episodeStreamEventParams = {
        episodeId,
        episodeNumber,
        seasonNumber,
        showId,
        showTitle,
        genres: (genres || []).map(({ slug }) => slug).join(',')
      };

      // GA stream start event
      sendGAEvent({ ...GAstreamedEpisodeStart(episodeStreamEventParams) });

      // Intercom stream start event
      sendIntercomEvent({ ...ICstreamedEpisodeStart(episodeStreamEventParams) });

      // FB Pixel stream start event
      sendFBPixelCustomEvent({ ...FBstreamedEpisodeStart(episodeStreamEventParams) });

      // Genre stream events
      (genres || []).forEach((genre = {}) => {
        const genreStreamEventParams = {
          genreId: genre.id,
          genreSlug: genre.slug,
          genreTitle: genre.title,
          parentGenreId: genre?.parent?.id,
          parentGenreTitle: genre?.parent?.title
        };

        sendGAEvent({ ...GAstreamedGenreStart(genreStreamEventParams) });

        sendIntercomEvent({ ...ICstreamedGenreStart(genreStreamEventParams) });
      });
    }
  }, [currentTrackStreamStartSentRef, sendGAEvent, sendIntercomEvent, sendFBPixelCustomEvent]);
  
  const handleSendStreamEvent = useCallback(({ isClosingBrowser = false, episode } = {}) => {
    const {
      id: episodeId,
      episode_number: episodeNumber,
      season: seasonNumber,
      genres
    } = episode;
    
    const {
      streamedTimeRanges,
      totalStreamingTimeSeconds,
      percentageProgress,
      initialBufferTimeSeconds,
      totalBufferTimeSeconds
    } = (isClosingBrowser ? getCurrentTrackStats() : getPreviousTrackStats());

    if (episodeId && totalStreamingTimeSeconds > 1) {
      const episodeShow = (episode || {}).show;
      const { id: showId, title: showTitle } = episodeShow;

      const episodeStreamEventParams = {
        episodeId,
        episodeNumber,
        seasonNumber,
        showId,
        showTitle,
        genres: (genres || []).map(({ slug }) => slug).join(','),
        percentageProgress,
        totalListeningTimeSeconds: totalStreamingTimeSeconds
      };

      // Keakie stream event
      sendStreamEvent({
        params: {
          episodeId: episode.id,
          streamingContext: currentStreamingContext,
          initialBufferTimeSeconds,
          totalBufferTimeSeconds,
          streamedTimeRanges,
          totalStreamingTimeSeconds
        },
        isClosingBrowser
      }).then(() => {
        if (showId && !isClosingBrowser) {
          // Clear the channel user edge for this episode as it may have updated after sending the stream event
          clearShowEdgeById({
            id: showId,
            queryClient
          });
        }
      });

      // GA stream event
      sendGAEvent({
        ...GAstreamedEpisode(episodeStreamEventParams),
        isClosingBrowser
      });

      // Intercom stream event
      sendIntercomEvent({
        ...ICstreamedEpisode(episodeStreamEventParams),
        isClosingBrowser
      });

      // FB Pixel stream event
      sendFBPixelCustomEvent({ ...FBstreamedEpisode(episodeStreamEventParams) });

      // Genre stream events
      (genres || []).forEach((genre = {}) => {
        const genreStreamEventParams = {
          genreId: genre.id,
          genreSlug: genre.slug,
          genreTitle: genre.title,
          parentGenreId: genre?.parent?.id,
          parentGenreTitle: genre?.parent?.title,
          totalListeningTimeSeconds: totalStreamingTimeSeconds
        };

        sendGAEvent({
          ...GAstreamedGenre(genreStreamEventParams),
          isClosingBrowser
        });

        sendIntercomEvent({
          ...ICstreamedGenre(genreStreamEventParams),
          isClosingBrowser
        });
      });
    }
  }, [
    currentStreamingContext,
    sendGAEvent,
    sendIntercomEvent,
    getCurrentTrackStats,
    getPreviousTrackStats,
    sendFBPixelCustomEvent,
    queryClient
  ]);

  useEffect(() => {
    const previousTrackKey = currentTrackKeyRef.current;
    const previousEpisode = currentEpisodeRef.current || {};
    const currentEpisodeIsValid = currentEpisode?.id;
    const streamStartAlreadySent = currentTrackStreamStartSentRef.current;

    if (trackKey !== previousTrackKey) {
      // The track has changed, so handle events for this

      if (previousTrackKey && previousEpisode.id) {
        // If there was a previous episode, send a stream event for it
        handleSendStreamEvent({
          isClosingBrowser: false,
          episode: previousEpisode
        });
      }

      // The track has changed, so we need to mark the stream start event as unsent
      currentTrackStreamStartSentRef.current = false;

      if (!isPaused && currentEpisodeIsValid) {
        // If the track is playing, we should sent the stream start event now
        handleSendStreamStartEvent({ episode: currentEpisode });
      }

      // Update the current episode refs
      currentEpisodeRef.current = currentEpisode;
      currentTrackKeyRef.current = trackKey;
    } else if (!isPaused && currentEpisodeIsValid && !streamStartAlreadySent) {
      // The track is unchanged, but the user began playback for an episode which is yet to have a stream start event sent
      handleSendStreamStartEvent({ episode: currentEpisode });
    }
  }, [
    currentTrackKeyRef,
    currentEpisodeRef,
    currentTrackStreamStartSentRef,
    trackKey,
    currentEpisode,
    isPaused,
    handleSendStreamEvent,
    handleSendStreamStartEvent
  ]);

  const sendCloseWindowStreamEvent = useCallback(() => {
    const episode = currentEpisodeRef.current || {};

    // Do not send the close event if already fired.
    // This ensures multiple event listeners do not repeat each other.
    if (episode.id && !browserCloseStreamSentRef.current) {
      browserCloseStreamSentRef.current = true;

      handleSendStreamEvent({
        episode,
        isClosingBrowser: true
      });
    }
  }, [handleSendStreamEvent]);

  const handlePageHide = useCallback((event) => {
    // If event.persisted is true, the page was frozen
    // If event.persisted is false, the page will be unloaded
    if (!event.persisted) {
      sendCloseWindowStreamEvent();
    }
  }, [sendCloseWindowStreamEvent]);
  
  // Attach listeners to the window so that we send a stream event when the user closes the page
  // MDN now recommends use of the pagehide event for sendBeacon
  // https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onunload
  useEffect(() => {
    window.addEventListener('pagehide', handlePageHide);
    
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [handlePageHide]);
};

export default useAudioStreamEvents;